/**
 * CanvasEcritureBandeau — ce que l'avocat voit sur SON document pendant qu'Hector
 * le modifie (#855, refondu le 2026-08-13).
 *
 * ## Un bandeau, plus un voile
 *
 * La première version posait un calque semi-transparent sur la zone d'édition.
 * Deux défauts, et le second était le pire : il cachait le travail. L'avocat
 * demande à Hector de rédiger *pour voir le texte apparaître* — masquer le
 * document au moment précis où il se remplit retire le seul retour qui compte.
 * Le calque protégeait donc un document que plus personne ne regardait.
 *
 * Le bandeau dit la même chose sans rien cacher : il s'insère **au-dessus** de
 * la zone d'édition, en flux, et pousse le document au lieu de le recouvrir. La
 * barre d'outils SuperDoc et le texte restent entiers.
 *
 * ## Pourquoi le document reste modifiable
 *
 * Un réflexe voudrait le figer. Ce n'est pas nécessaire, et c'est vérifiable :
 * toute mutation agent est clôturée par la barrière de révision de
 * `canvasAgentRequestGuard` — une frappe de l'avocat fait avancer la révision du
 * document, et la mutation suivante est refusée `STALE_CANVAS_REVISION` **avant**
 * le moindre appel à la Document API, avec la consigne de réinspecter. Une
 * frappe simultanée ne peut donc pas faire écrire Hector au mauvais endroit ;
 * elle lui coûte un aller-retour d'inspection.
 *
 * (Le voile ne figeait rien non plus, d'ailleurs : il arrêtait le pointeur, pas
 * le clavier. Un curseur déjà posé continuait de taper dessous.)
 *
 * Le bandeau se contente donc de **le déconseiller**, ce qui est exactement le
 * niveau de contrainte que la mécanique justifie.
 *
 * ## Quand il apparaît — et pourquoi pas plus tôt
 *
 * À la **première écriture** de l'agent, jamais à la seule réservation d'une
 * session. Les compteurs exacts `writer` et `mutation` du runtime s'élèvent au
 * point d'écriture, tandis qu'une inspection seule ne modifie rien.
 *
 * ⚠️ Il n'a **pas** de région live à lui : une région insérée en même temps que
 * son contenu n'est pas annoncée de façon fiable. L'annonce vit dans la région
 * permanente du bandeau du canvas (`canvas-activity-announcement`, `RedactionPanel`).
 */

import * as React from 'react';
import { PenLine, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CanvasEcritureBandeauProps {
  /**
   * Arrête le tour en cours. Absent quand la session temps réel du porteur est
   * introuvable : le bandeau reste alors informatif — mieux vaut un bouton qui
   * manque qu'un bouton qui ment.
   */
  onArreter?: () => void;
}

export function CanvasEcritureBandeau({
  onArreter,
}: CanvasEcritureBandeauProps): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-900"
      data-testid="canvas-ecriture-bandeau"
    >
      <PenLine className="h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden="true" />
      <span className="min-w-0">
        Hector modifie ce document. Mieux vaut ne pas y écrire en même temps.
      </span>
      {onArreter && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-6 shrink-0 px-2 text-xs text-amber-900 hover:bg-amber-100"
          onClick={onArreter}
        >
          <Square className="mr-1.5 h-3 w-3" aria-hidden="true" />
          Arrêter la rédaction
        </Button>
      )}
    </div>
  );
}
